import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Sofa, Plus } from 'lucide-react'
import { useFurniture } from '@/hooks/useFurniture'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states'
import { Tabs } from '@/components/ui/tabs'
import { FurnitureTile } from '@/components/FurnitureTile'
import { FurnitureGroupsPanel } from '@/components/FurnitureGroupsPanel'
import { TableThumbCell, TableThumbHead } from '@/components/TableThumbCell'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell, TableEmptyRow } from '@/components/ui/table'
import { FilterMenu, toggleFilterValue, type FilterMenuOption } from '@/components/ui/filter-menu'
import { Badge } from '@/components/ui/badge'
import { Pagination } from '@/components/ui/pagination'
import { serverPage } from '@/lib/pagination'
import type { VenueDto } from '@lib/dto/venue.dto'
import type { FurnitureItemDto, FurnitureItemListDto } from '@lib/dto/furniture-item.dto'
import { UNTAGGED } from '@lib/helpers/furniture-tag'
import { useFurnitureTags } from '@/hooks/useFurnitureTags'

/**
 * A venue's furniture: its catalog of pieces, and the arrangements built from
 * them.
 *
 * A section of the venue's settings rather than a page of its own, so it sits
 * beside Inventory — the two are the same subject split in half. The catalog
 * says what a piece *is*; inventory says how many of it the venue has and
 * whether any are pinned to one space. Somebody adding a chair needs both, and
 * they used to be a nav entry apart.
 */
export function VenueFurnitureBody({
  venue,
  canEdit,
}: {
  venue: VenueDto
  /** Edit adds designing new pieces, changing them, and building arrangements; without it the catalog is read. */
  canEdit: boolean
}) {
  const navigate = useNavigate()

  /**
   * The venue whose settings these are — not whichever one the session happens
   * to be focused on. Reading it from the prop is what lets an organization
   * admin open a venue's settings and manage its furniture without first
   * entering the venue.
   */
  const venueId = venue.id
  const [tab, setTab] = React.useState<'items' | 'groups'>('items')
  const [page, setPage] = React.useState(1)
  const [tagFilter, setTagFilter] = React.useState<string[]>([])
  // Tags belong to a venue, so a filter does not survive switching to another
  // venue's settings: it would otherwise sit there as a chip for a tag the new
  // venue has never used, over an empty table.
  React.useEffect(() => {
    setTagFilter([])
    setPage(1)
  }, [venueId])
  // The sentinel is the browser's own marker; the API is asked with a separate
  // parameter, because a tag could legitimately be called anything.
  const wantsUntagged = tagFilter.includes(UNTAGGED)
  const furnitureQuery = useFurniture(
    page,
    venueId,
    tagFilter.filter((t) => t !== UNTAGGED),
    wantsUntagged,
  )
  const tagsQuery = useFurnitureTags(venueId)

  /**
   * "Not set" is marked exclusive: it and a tag are a contradiction — nothing
   * carries a tag and no tags at once — so picking either drops the other. Its
   * value is a sentinel that cannot be a tag, so a venue naming a tag
   * "untagged" does not collide with it.
   */
  const tagOptions: FilterMenuOption[] = [
    { value: UNTAGGED, label: 'Not set', exclusive: true },
    ...(tagsQuery.data?.tags ?? []).map((t) => ({ value: t, label: t })),
  ]

  function toggleTagFilter(tag: string): void {
    setTagFilter((current) => toggleFilterValue(current, tag, tagOptions))
    setPage(1)
  }

  function clearTagFilter(): void {
    setTagFilter([])
    setPage(1)
  }

  function renderList() {
    if (furnitureQuery.isPending) return <LoadingState label="Loading furniture…" />
    if (furnitureQuery.isError) {
      return <ErrorState message={requestErrorMessage(furnitureQuery.error)} onRetry={() => void furnitureQuery.refetch()} />
    }
    const list: FurnitureItemListDto = furnitureQuery.data
    // `total`, not the row count: an out-of-range page comes back with no rows,
    // and keying the empty state off that would tell somebody their catalog was
    // empty when it is only this page that is.
    if (list.total === 0) {
      // A filter matching nothing keeps the table, because the filter lives in
      // its header band: replacing the whole table with a paragraph would take
      // the filter away at exactly the moment somebody needs to widen it. See
      // `TableEmptyRow`. A genuinely empty catalog is a different thing and
      // still gets the full empty state below — there the useful action is
      // adding a piece, and it comes with one.
      if (tagFilter.length > 0) {
        return renderTable(
          list,
          <TableBody>
            <TableEmptyRow colSpan={3}>
              {describeEmptyFilter(tagFilter)}{' '}
              <button
                type="button"
                onClick={clearTagFilter}
                className="underline underline-offset-2 outline-none transition-colors hover:text-ink focus-visible:focus-ds"
              >
                Show all furniture
              </button>
            </TableEmptyRow>
          </TableBody>,
        )
      }
      return (
        <EmptyState
          icon={Sofa}
          title="No furniture yet"
          description={canEdit ? 'Design your first catalog item to stock it at a venue.' : 'No catalog items yet.'}
          action={
            canEdit ? (
              <Button onClick={() => navigate(`/furniture/items/new?venue=${venueId}`)}>
                <Plus className="h-4 w-4" /> New item
              </Button>
            ) : undefined
          }
        />
      )
    }
    return renderTable(
      list,
      <TableBody>
        {/* The API already sliced these — `shown` is only the arithmetic the
            readout needs. */}
        {list.items.map((item) => (
          // Open for reading too: the item's page shows it read-only to someone
          // who can see the catalog but not change it.
          <ItemRow key={item.id} item={item} onOpen={() => navigate(`/furniture/items/${item.id}?venue=${venueId}`)} />
        ))}
      </TableBody>,
    )
  }

  /**
   * The table shell: heading, the filter and the page's own button, columns and
   * pagination. Shared so a filter that matches nothing keeps all of it — the
   * filter lives in the band, and taking the band away leaves somebody unable
   * to widen the search that just came back empty.
   */
  function renderTable(list: FurnitureItemListDto, body: React.ReactNode) {
    // The rows are the page the API returned; `total` is the whole catalog, which
    // is what the bar's readout is about.
    const shown = serverPage(page, list.total, list.items.length)
    return (
      <Table
        // `total`, not the row count: the rows are one page, and a band that
        // said "15 items" on page 2 of 65 would be counting the wrong thing.
        heading={`${String(list.total)} item${list.total === 1 ? '' : 's'}`}
        actions={
          <>
            <FilterMenu
              label="Tags"
              options={tagOptions}
              selected={tagFilter}
              onToggle={toggleTagFilter}
              onClear={clearTagFilter}
            />
            {canEdit ? (
              <Button variant="ghost" size="sm" onClick={() => navigate(`/furniture/items/new?venue=${venueId}`)}>
                <Plus className="h-4 w-4" /> New item
              </Button>
            ) : null}
          </>
        }
        footer={
          <Pagination
            page={shown.page}
            pageCount={shown.pageCount}
            from={shown.from}
            to={shown.to}
            total={shown.total}
            unit="items"
            onPage={setPage}
          />
        }
      >
        <TableHeader>
          <TableThumbHead />
          <TableHead>Item</TableHead>
          <TableHead>Tag</TableHead>
        </TableHeader>
        {body}
      </Table>
    )
  }

  return (
    <div className="space-y-6">
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v === 'groups' ? 'groups' : 'items')}
        items={[
          { value: 'items', label: 'Items' },
          { value: 'groups', label: 'Groups' },
        ]}
      />

      {tab === 'items' ? (
        <>
          {renderList()}
        </>
      ) : (
        <FurnitureGroupsPanel venueId={venueId} canEdit={canEdit} />
      )}
    </div>
  )
}

/** What to say when a filter matches nothing, in the filter's own words. */
function describeEmptyFilter(selected: readonly string[]): string {
  const named = selected.filter((t) => t !== UNTAGGED)
  if (named.length === 0) return 'Every piece at this venue carries a tag.'
  const quoted = named.map((t) => `"${t}"`)
  if (quoted.length === 1) return `No piece here is tagged ${quoted[0] ?? ''}.`
  // "and", because every tag has to be on the piece — a list joined with "or"
  // would describe a wider search than the one that just came back empty.
  const list = `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1] ?? ''}`
  return `No piece here carries ${list}.`
}

/**
 * One catalog row: the item's photograph, its name, where it is stocked.
 *
 * An item with no photograph shows its drawn shape rather than a stand-in
 * photograph — for furniture the diagram *is* the likeness, and a default
 * photograph of somebody else's chair would be a lie. That is why the shape is
 * centered on the pale ground instead of bled like a real picture: a line
 * drawing stretched to the row's edges reads as a mistake.
 *
 * The whole row opens the editor, for the same reason the events table does it:
 * the row is about one thing, and making only its name clickable puts a
 * five-pixel target in a forty-pixel row.
 */
function ItemRow({
  item,
  onOpen,
}: {
  item: FurnitureItemDto
  onOpen: (() => void) | undefined
}) {
  return (
    <TableRow
      className={onOpen ? 'cursor-pointer' : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onOpen()
              }
            }
          : undefined
      }
      tabIndex={onOpen ? 0 : undefined}
    >
      <TableThumbCell
        src={item.imageUrl}
        // The piece's drawing on the plan grid, filling the frame — the same
        // tile the editor's inventory shows it on.
        fallback={<FurnitureTile primitives={item.primitives} fill />}
      />
      {/* "Available at" is gone: every piece on this page belongs to the venue
          being looked at, so the column said the same thing on every row. */}
      <TableCell className="font-medium">{item.name}</TableCell>
      <TableCell>
        {/* The same chips the editor holds, so a tag looks like itself wherever
            it is read. An untagged piece stays plain text: a badge saying "Not
            set" would give absence the weight of a value. */}
        {item.tags.length === 0 ? (
          <span className="text-ink-muted">Not set</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {item.tags.map((t) => (
              <Badge key={t}>{t}</Badge>
            ))}
          </span>
        )}
      </TableCell>
    </TableRow>
  )
}
